/**
 * Environment-scoped analytics configuration.
 *
 * DesignMyNight's own GA4 property and GTM container are supplied per
 * environment as secrets, which are resolved only once the container is
 * running, so they are read from the server over `/analytics-ids` rather than
 * from the bundle. No IDs are hard coded: when a value is absent for the
 * current environment the corresponding destination is simply never injected,
 * so local development and unconfigured environments stay silent rather than
 * polluting a live property.
 */
import * as rpc from "../../common/jsonRpcRoutes";

export interface DmnAnalyticsIds {
  /** DesignMyNight's GA4 measurement ID, or `null` when not configured. */
  readonly measurementId: string | null;
  /** DesignMyNight's GTM container ID, or `null` when not configured. */
  readonly containerId: string | null;
}

/**
 * Normalises a GA4 measurement or GTM container ID, wherever it came from —
 * our own configuration, an embedder's attributes or the query string.
 *
 * The shape of these IDs is owned by Google and known to change, so no format
 * check is applied: whatever is configured is passed through to Google.
 *
 * @returns the trimmed, upper-cased ID, or `null` when it is absent or blank.
 */
export function normaliseAnalyticsId(value: string | null | undefined): string | null {
  const candidate = value?.trim().toUpperCase();
  if (!candidate) return null;
  return candidate;
}

const NO_DMN_IDS: DmnAnalyticsIds = { measurementId: null, containerId: null };

let dmnIds: Promise<DmnAnalyticsIds> | null = null;

/**
 * DesignMyNight's own analytics IDs, fetched from the server on first ask and
 * reused by every later one.
 *
 * `GA4_MEASUREMENT_ID` and `GTM_CONTAINER_ID` are set per environment (local,
 * QA, playpen, production) and are intentionally empty until the properties are
 * created. A failed request yields both IDs absent, so an unreachable server
 * silences our destinations rather than breaking the journey.
 */
export function getDmnAnalyticsIds(): Promise<DmnAnalyticsIds> {
  dmnIds ??= (async () => {
    try {
      const { measurementId, containerId } = await rpc.call("/analytics-ids", {});

      return {
        measurementId: normaliseAnalyticsId(measurementId),
        containerId: normaliseAnalyticsId(containerId),
      };
    } catch {
      return NO_DMN_IDS;
    }
  })();

  return dmnIds;
}

/** Forgets the fetched IDs so the next ask reaches the server again. Test-only. */
export function resetDmnAnalyticsIds(): void {
  dmnIds = null;
}

/**
 * Origins the analytics envelope may be posted to when the checkout is framed.
 *
 * Configured per environment as a comma-separated list in
 * `VITE_ANALYTICS_EMBEDDER_ORIGINS`, because the origin of the page framing us
 * cannot be read from inside a cross-origin frame and `document.referrer` is
 * empty or stale in several legitimate cases. Malformed entries are dropped, and
 * each value is normalised to a bare origin so a full URL configured by mistake
 * still works.
 */
export function getAllowedEmbedderOrigins(): readonly string[] {
  const configured = import.meta.env.VITE_ANALYTICS_EMBEDDER_ORIGINS?.split(",") ?? [];

  const origins = configured.map(normaliseOrigin).filter((origin): origin is string => origin !== null);

  return [...new Set(origins)];
}

/** @returns the bare origin of a configured value, or `null` when unusable. */
function normaliseOrigin(value: string): string | null {
  const candidate = value.trim();
  if (!candidate) return null;

  try {
    const { origin } = new URL(candidate);
    return origin === "null" ? null : origin;
  } catch {
    return null;
  }
}

/** Whether analytics is switched off wholesale for this environment. */
export function isAnalyticsDisabled(): boolean {
  return import.meta.env.VITE_ANALYTICS_DISABLED === "true";
}
